import { Menu, Box, IconButton, Typography, Button, useTheme } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { useState, useEffect } from "react";

interface MonthMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  selectedDate: Date;
  handleMonthSelect: (year: number, month: number, closeMenu?: boolean) => void;
  isMonthDisabled: (year: number, monthIndex: number) => boolean;
}

export const MonthMenu = ({
  anchorEl,
  open,
  onClose,
  selectedDate,
  handleMonthSelect,
  isMonthDisabled
}: MonthMenuProps) => {
  const theme = useTheme();
  const [viewingYear, setViewingYear] = useState(selectedDate.getFullYear());

  useEffect(() => {
    if (open) {
      setViewingYear(selectedDate.getFullYear());
    }
  }, [open, selectedDate]);

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      transformOrigin={{ vertical: "top", horizontal: "center" }}
      PaperProps={{
        sx: {
          width: 320,
          p: 2,
          borderRadius: 2,
          boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.1)",
        },
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <IconButton onClick={() => setViewingYear(viewingYear - 1)} size="small">
          <ChevronLeft />
        </IconButton>
        <Typography variant="h6" sx={{ fontWeight: "medium" }}>
          {viewingYear}
        </Typography>
        <IconButton onClick={() => setViewingYear(viewingYear + 1)} size="small">
          <ChevronRight />
        </IconButton>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          gap: 1,
          mb: 3,
        }}
      >
        {Array.from({ length: 12 }, (_, i) => {
          const monthName = format(new Date(2000, i, 1), "MMM", { locale: es });
          const isCurrentMonth = viewingYear === selectedDate.getFullYear() && i === selectedDate.getMonth();
          const disabled = isMonthDisabled(viewingYear, i);

          return (
            <Button
              key={i}
              variant={isCurrentMonth ? "contained" : "text"}
              onClick={() => handleMonthSelect(viewingYear, i)}
              disabled={disabled}
              sx={{
                width: "30%",
                minWidth: 80,
                py: 1.5,
                borderRadius: 2,
                color: disabled ? "text.disabled" : isCurrentMonth ? "primary.contrastText" : "text.primary",
                backgroundColor: isCurrentMonth ? theme.palette.primary.main : "transparent",
                "&:hover": {
                  backgroundColor: isCurrentMonth ? theme.palette.primary.main : "rgba(0, 0, 0, 0.1)",
                },
              }}
            >
              {monthName}
            </Button>
          );
        })}
      </Box>

      <Button
        variant="outlined"
        onClick={() => {
          const now = new Date();
          handleMonthSelect(now.getFullYear(), now.getMonth());
        }}
        fullWidth
        sx={{
          borderColor: theme.palette.primary.main,
          color: theme.palette.primary.main,
          textTransform: "none",
          py: 1,
          borderRadius: 2,
        }}
      >
        Mes Actual
      </Button>
    </Menu>
  );
};
